import React, { useState, useMemo, useEffect, useCallback } from 'react';
import BaseModal from 'src/components/primitives/BaseModal/BaseModal';
import useModal from './hooks/useModal';
import { CollateralModalProps } from './CollateralModal/types';
import MarketIcon, { getMarketIconSrc } from '../Utils/MarketIcon';
import { useTheme } from 'next-themes';
import { DriftTheme } from 'src/environmentVariables/EnvironmentVariables';
import QRCode from 'qrcode.react';
import {
	Checkmark,
	Copy,
	ChevronUp,
	ChevronDown,
	Warning,
} from '@drift-labs/icons';
import Tooltip from 'src/components/primitives/Tooltip/Tooltip';
import Select from 'src/components/primitives/Select/Select';
import { ComboBox } from 'src/components/primitives/ComboBox/ComboBox';
import { TOOLTIP_TRIGGER_DECORATION_CLASSNAME } from 'src/components/primitives/Tooltip/consts';
import useFunXyzActions, {
	SOLANA_TARGET_ASSETS,
	SupportedTargetAsset,
	FunXyzAsset,
} from 'src/hooks/useFunXyzActions';
import InlineLoadingBar from 'src/components/InlineLoadingBar/InlineLoadingBar';
import useWalletIsConnected from 'src/hooks/useWalletIsConnected';
import Divider from 'src/components/primitives/Divider/Divider';
import Button from 'src/components/primitives/Button/Button';
import { cj } from 'src/utils/classNames';
import { getColorVar } from 'src/utils/theme/colorVars';
import usePostHogCapture from 'src/hooks/posthog/usePostHogCapture';
import useDriftAccountsStore from 'src/stores/useDriftAccountsStore';
import { MAIN_POOL_ID, NEW_ACCOUNT_BASE_RENT, UIMarket } from '@drift/common';
import { getCleanedMarketSymbol } from 'src/utils/market';
import SubAccountSelector from './CollateralModal/components/SubAccountSelector';
import useTickedAllSubAccounts from 'src/hooks/useTickedAllSubAccounts';

const COVALENT_LOGO_BASE = 'https://logos.covalenthq.com/tokens';
const IMAGE_PROXY = '/api/image-proxy';

// Track failed image URLs to avoid re-fetching (keeps only strings, minimal memory)
const failedImageUrls = new Set<string>();

function proxyExternalImageUrl(url: string): string {
	if (!url.startsWith('http://') && !url.startsWith('https://')) return url;
	return `${IMAGE_PROXY}?url=${encodeURIComponent(url)}`;
}

interface AssetOption {
	symbol: string;
	name: string;
	logo?: string;
	availableChainIds: string[];
	/** chainId -> token contract address for Covalent logo fallback */
	addressByChainId: Record<string, string>;
}

interface ChainOption {
	id: string;
	name: string;
}

export interface DepositFromOtherSourcesModalProps extends CollateralModalProps {
	returnToDepositModal?: boolean;
	createNewSubaccount?: boolean;
	newSubaccountDepositAmount?: string;
	newSubaccountDepositSource?: string;
	newSubaccountCustomName?: string;
}

// Receive assets on Solana (what the user gets on Drift)
const RECEIVE_ASSETS = [
	{ symbol: 'USDC', name: 'USD Coin' },
	{ symbol: 'SOL', name: 'Solana' },
];

// Chain ID to MarketIcon symbol mapping
const CHAIN_ICON_MAP: Record<string, string> = {
	'1': 'ETH', // Ethereum
	'10': 'OP', // Optimism
	'56': 'BNB', // BNB Smart Chain
	'137': 'POL', // Polygon
	'143': '1KMON', // Monad
	'999': 'HYPE', // HyperEVM
	'2741': 'ABSTRACT', // Abstract
	'8453': 'BASE', // Base
	'42161': 'ARB', // Arbitrum
	'3586256': 'LIT', // Lighter
	'5064014': 'ETHEREAL', // Ethereal
	'8253038': 'BTC', // Bitcoin
	'728126428': 'TRON', // Tron
	'1151111081099710': 'SOL', // Solana
};

// Whitelist of allowed assets per chain
const ALLOWED_ASSETS_BY_CHAIN: Record<string, Set<string>> = {
	'1': new Set([
		// Ethereum
		'WBTC',
		'PAXG',
		'BUSD',
		'USDe',
		'DAI',
		'AAVE',
		'USDP',
		'USDC',
		'ARB',
		'WETH',
		'ETH',
		'cbBTC',
		'USDT',
		'USDS',
	]),
	'10': new Set([
		// Optimism
		'USD₮0',
		'USDC',
		'WETH',
		'OP',
		'USDe',
		'USDT',
		'DAI',
		'ETH',
	]),
	'56': new Set([
		// BNB Smart Chain
		'DAI',
		'ETH',
		'USDT',
		'USDe',
		'BTCB',
		'USDC',
		'BNB',
		'WBNB',
		'BUSD',
	]),
	'137': new Set([
		// Polygon
		'USDC',
		'WETH',
		'POL',
		'USDT',
	]),
	'143': new Set([
		// Monad
		'WMON',
		'USDC',
		'MON',
		'USDT0',
	]),
	'999': new Set([
		// HyperEVM
		'WHYPE',
		'USDe',
		'hbHYPE',
		'UBTC',
		'USD₮0',
		'UETH',
		'HYPE',
		'USDC',
		'XAUt0',
		'kHYPE',
		'stHYPE',
	]),
	'2741': new Set([
		// Abstract
		'USDT',
		'WETH',
		'USDC',
		'ETH',
	]),
	'8453': new Set([
		// Base
		'uXRP',
		'WETH',
		'DAI',
		'USDe',
		'USDS',
		'USDC',
		'AERO',
		'ETH',
		'cbBTC',
		'USDbC',
		'USDT',
	]),
	'42161': new Set([
		// Arbitrum
		'WBTC',
		'USDe',
		'WETH',
		'ARB',
		'DAI',
		'ETH',
		'USDC',
		'USDT',
	]),
	'3586256': new Set([
		// Lighter
		'USDC',
	]),
	'5064014': new Set([
		// Ethereal
		'USDe',
	]),
	'8253038': new Set([
		// Bitcoin
		'BTC',
	]),
	'728126428': new Set([
		// Tron
		'USDT',
	]),
	'1151111081099710': new Set([
		// Solana
		'SOL',
		'TRUMP',
		'USDe',
		'USDC',
		'USDT',
		'CASH',
	]),
};

function isAssetAllowed(chainId: string, symbol: string): boolean {
	const allowedSymbols = ALLOWED_ASSETS_BY_CHAIN[chainId];
	return allowedSymbols ? allowedSymbols.has(symbol) : false;
}

function getChainIconSymbol(chainId: string): string | undefined {
	return CHAIN_ICON_MAP[chainId];
}

function getTokenIconUrl(chainId: string, address: string): string | null {
	// Covalent only supports EVM chains (0x + 40 hex). Skip Solana/base58 addresses.
	const isEvmAddress =
		(address.startsWith('0x') && address.length === 42) ||
		(!address.startsWith('0x') && address.length === 40);
	if (!isEvmAddress) return null;
	const addr = address.startsWith('0x')
		? address.toLowerCase()
		: `0x${address.toLowerCase()}`;
	return `${COVALENT_LOGO_BASE}/${chainId}/${addr}.png`;
}

function LetterFallback({
	symbol,
	className,
}: {
	symbol: string;
	className: string;
}) {
	const letter = (symbol.slice(0, 2).toUpperCase() || '?').replace(/\d/g, '');
	return (
		<div
			className={cj(
				className,
				'bg-container-bg-secondary flex items-center justify-center rounded-full typo-b5 font-semibold text-text-secondary'
			)}
		>
			{letter}
		</div>
	);
}

// Asset icon: Drift icon (when ticker matches) → Fun.xyz logo → Covalent (chainId+address) → letter fallback
// Prefer Drift's icons for assets we already have (ETH, HYPE, ARB, etc.); only use image proxy when needed.
const AssetIcon = ({
	logo,
	symbol,
	chainId,
	address,
	className = 'size-5',
}: {
	logo?: string;
	symbol: string;
	chainId?: string;
	address?: string;
	className?: string;
}) => {
	const theme = (useTheme().theme as DriftTheme) ?? DriftTheme.dark;
	const [driftIconFailed, setDriftIconFailed] = useState(false);
	const [proxyError, setProxyError] = useState(false);
	const tokenIconUrl =
		chainId && address ? getTokenIconUrl(chainId, address) : null;

	const driftIconSrc = getMarketIconSrc({
		baseSymbol: symbol,
		theme,
		grey: false,
	});

	const rawProxySrc = logo ?? tokenIconUrl ?? null;
	const isProxyKnownFailed =
		(rawProxySrc !== null && failedImageUrls.has(rawProxySrc)) || proxyError;
	const proxyImgSrc =
		rawProxySrc !== null && !isProxyKnownFailed
			? proxyExternalImageUrl(rawProxySrc)
			: null;

	return (
		<div
			className={cj(
				'relative shrink-0 overflow-hidden rounded-full',
				className
			)}
		>
			<LetterFallback symbol={symbol} className={className} />
			{!driftIconFailed && (
				<img
					src={driftIconSrc}
					alt={symbol}
					className="absolute inset-0 size-full rounded-full object-cover"
					loading="lazy"
					decoding="async"
					onLoad={(e) => {
						e.currentTarget.style.opacity = '1';
					}}
					onError={() => setDriftIconFailed(true)}
					style={{ opacity: 0 }}
				/>
			)}
			{driftIconFailed && proxyImgSrc && (
				<img
					src={proxyImgSrc}
					alt={symbol}
					className="absolute inset-0 size-full rounded-full object-cover"
					loading="lazy"
					decoding="async"
					referrerPolicy="no-referrer"
					onLoad={(e) => {
						e.currentTarget.style.opacity = '1';
					}}
					onError={() => {
						if (rawProxySrc) {
							failedImageUrls.add(rawProxySrc);
							setProxyError(true);
						}
					}}
					style={{ opacity: 0 }}
				/>
			)}
		</div>
	);
};

const DepositFromOtherSourcesModal = ({
	collateralIndex,
	collateralPoolId,
	targetAccountKey,
	defaultLargestBalance,
	returnToDepositModal = false,
	createNewSubaccount = false,
	newSubaccountDepositAmount,
	newSubaccountDepositSource,
	newSubaccountCustomName,
}: DepositFromOtherSourcesModalProps) => {
	const { hide, show } = useModal();
	const [copied, setCopied] = useState(false);
	const [isHowToDepositExpanded, setIsHowToDepositExpanded] = useState(false);
	const isWalletConnected = useWalletIsConnected();
	const [assetSearchQuery, setAssetSearchQuery] = useState('');
	const [assetDropdownOpen, setAssetDropdownOpen] = useState(false);
	const { captureEvent } = usePostHogCapture();
	const userAccountNotInitialized = useDriftAccountsStore(
		(s) => s.userAccountNotInitialized
	);
	const currentUserKey = useDriftAccountsStore((s) => s.currentUserKey);

	const [selectedTargetAccountKey, setSelectedTargetAccountKey] = useState(
		createNewSubaccount ? '' : (targetAccountKey ?? currentUserKey ?? '')
	);

	const selectedTargetAccount = useDriftAccountsStore(
		(s) => s.accounts[selectedTargetAccountKey ?? '']
	);

	const selectedPoolId =
		selectedTargetAccount?.poolId ?? collateralPoolId ?? MAIN_POOL_ID;

	const poolId = selectedTargetAccount?.poolId ?? collateralPoolId ?? null;

	// Only show receive assets that exist in the target pool; Fun.xyz supports USDC and SOL on Solana
	const availableReceiveAssets = useMemo(() => {
		if (poolId === null) return [];
		const poolSymbols = new Set(
			UIMarket.spotMarkets
				.filter((m) => m.poolId === poolId)
				.map((m) => getCleanedMarketSymbol(m.symbol))
		);
		return RECEIVE_ASSETS.filter((ra) => poolSymbols.has(ra.symbol));
	}, [poolId]);

	const hasSingleReceiveAsset = availableReceiveAssets.length === 1;

	const filterAccountForFunXyz = useCallback(
		(account: { poolId: number }) => account.poolId === MAIN_POOL_ID,
		[]
	);

	const allSubaccounts = useTickedAllSubAccounts();
	const eligibleAccounts = useMemo(
		() => allSubaccounts.filter(filterAccountForFunXyz),
		[allSubaccounts, filterAccountForFunXyz]
	);

	useEffect(() => {
		if (
			eligibleAccounts.length === 0 ||
			eligibleAccounts.some((a) => a.userKey === selectedTargetAccountKey)
		)
			return;
		setSelectedTargetAccountKey(eligibleAccounts[0].userKey);
	}, [eligibleAccounts, selectedTargetAccountKey]);

	// Fun.xyz UDA hook - pass targetAccountKey so deposit address is for the selected account, not active
	const {
		supportedAssets,
		chains,
		createDepositAddress,
		isCreatingAddress,
		receivingAddress,
		selectedSourceAsset,
		isLoadingAssets,
		loadSupportedAssets,
		fetchQuote,
		quote,
		isGettingQuote,
	} = useFunXyzActions({
		targetAccountKey: createNewSubaccount
			? undefined
			: selectedTargetAccountKey,
		forNewSubaccount: createNewSubaccount,
	});

	// Selection state
	const [selectedAssetSymbol, setSelectedAssetSymbol] = useState<string | null>(
		null
	);
	const [selectedChainId, setSelectedChainId] = useState<string | null>(null);
	const [selectedReceiveAsset, setSelectedReceiveAsset] = useState(
		RECEIVE_ASSETS[0]
	);

	// Build unique asset list across all chains (deduplicated by symbol)
	// Only include whitelisted assets, USDC goes first, then sort alphabetically
	const uniqueAssets = useMemo(() => {
		const assetMap = new Map<string, AssetOption>();

		for (const asset of supportedAssets) {
			// Skip assets not in the whitelist for their chain
			if (!isAssetAllowed(asset.chainId, asset.symbol)) {
				continue;
			}

			// Normalize symbol for deduplication (case-insensitive)
			const normalizedSymbol = asset.symbol.toUpperCase();
			const existing = assetMap.get(normalizedSymbol);

			if (existing) {
				if (!existing.availableChainIds.includes(asset.chainId)) {
					existing.availableChainIds.push(asset.chainId);
				}
				if (asset.address) {
					existing.addressByChainId[asset.chainId] = asset.address;
				}
				if (!existing.logo && asset.logo) {
					existing.logo = asset.logo;
				}
			} else {
				assetMap.set(normalizedSymbol, {
					symbol: asset.symbol,
					name: asset.name,
					logo: asset.logo,
					availableChainIds: [asset.chainId],
					addressByChainId: asset.address
						? { [asset.chainId]: asset.address }
						: {},
				});
			}
		}

		const assets = Array.from(assetMap.values());

		// Sort: USDC first, then alphabetically
		assets.sort((a, b) => {
			if (a.symbol.toUpperCase() === 'USDC') return -1;
			if (b.symbol.toUpperCase() === 'USDC') return 1;
			return a.symbol.localeCompare(b.symbol);
		});

		return assets;
	}, [supportedAssets]);

	// Build chain list with availability info based on selected asset
	const chainOptions = useMemo(() => {
		const selectedAsset = uniqueAssets.find(
			(a) => a.symbol === selectedAssetSymbol
		);
		const availableChainIds = new Set(selectedAsset?.availableChainIds ?? []);

		const chainList: (ChainOption & { isAvailable: boolean })[] = chains.map(
			(chain) => ({
				id: chain.chainId,
				name: chain.chainName,
				isAvailable: availableChainIds.has(chain.chainId),
			})
		);

		// Sort: available chains first, then unavailable
		chainList.sort((a, b) => {
			if (a.isAvailable && !b.isAvailable) return -1;
			if (!a.isAvailable && b.isAvailable) return 1;
			return a.name.localeCompare(b.name);
		});

		return chainList;
	}, [chains, selectedAssetSymbol, uniqueAssets]);

	// Filter assets by search query - only match ticker/symbol, not asset name
	const filteredAssets = useMemo(() => {
		if (!assetSearchQuery) return uniqueAssets;
		const query = assetSearchQuery.toLowerCase().trim();
		return uniqueAssets.filter((asset) =>
			asset.symbol.toLowerCase().includes(query)
		);
	}, [uniqueAssets, assetSearchQuery]);

	// Get selected asset object
	const selectedAsset = useMemo(() => {
		return uniqueAssets.find((a) => a.symbol === selectedAssetSymbol) ?? null;
	}, [uniqueAssets, selectedAssetSymbol]);

	// Get selected chain object
	const selectedChain = useMemo(() => {
		return chainOptions.find((c) => c.id === selectedChainId) ?? null;
	}, [chainOptions, selectedChainId]);

	// Load supported assets on mount
	useEffect(() => {
		loadSupportedAssets();
	}, [loadSupportedAssets]);

	// Set default asset (USDC) when assets load
	useEffect(() => {
		if (uniqueAssets.length > 0 && !selectedAssetSymbol) {
			// Default to USDC if available, otherwise first asset
			const defaultAsset =
				uniqueAssets.find((a) => a.symbol === 'USDC') ?? uniqueAssets[0];
			setSelectedAssetSymbol(defaultAsset.symbol);
		}
	}, [uniqueAssets, selectedAssetSymbol]);

	// Keep selected receive asset in sync with pool's available assets
	useEffect(() => {
		const current = selectedReceiveAsset.symbol;
		const isAvailable = availableReceiveAssets.some(
			(a) => a.symbol === current
		);
		if (!isAvailable && availableReceiveAssets[0]) {
			setSelectedReceiveAsset(availableReceiveAssets[0]);
		}
	}, [availableReceiveAssets, selectedReceiveAsset.symbol]);

	// Set default chain when asset changes (prefer ETH, then first available)
	useEffect(() => {
		if (!selectedAsset) return;

		// If current chain is available for the new asset, keep it
		if (
			selectedChainId &&
			selectedAsset.availableChainIds.includes(selectedChainId)
		) {
			return;
		}

		// Prefer Ethereum (chainId '1') if available for this asset
		const ethChainId = '1';
		if (selectedAsset.availableChainIds.includes(ethChainId)) {
			setSelectedChainId(ethChainId);
			return;
		}

		// Otherwise, select the first available chain
		const firstAvailableChain = chainOptions.find((c) => c.isAvailable);
		if (firstAvailableChain) {
			setSelectedChainId(firstAvailableChain.id);
		}
	}, [selectedAsset, selectedChainId, chainOptions]);

	// Create deposit address when selections change
	useEffect(() => {
		if (
			!isWalletConnected ||
			!selectedReceiveAsset ||
			!selectedAssetSymbol ||
			!selectedChainId ||
			availableReceiveAssets.length === 0 ||
			!availableReceiveAssets.some(
				(a) => a.symbol === selectedReceiveAsset.symbol
			)
		)
			return;

		const targetAssetKey = selectedReceiveAsset.symbol as SupportedTargetAsset;
		if (!SOLANA_TARGET_ASSETS[targetAssetKey]) return;

		// Find the original FunXyzAsset for the selected asset + chain combo
		const funXyzAsset = supportedAssets.find(
			(a) =>
				a.symbol === selectedAssetSymbol &&
				String(a.chainId) === String(selectedChainId)
		);

		if (funXyzAsset) {
			void (async () => {
				try {
					await createDepositAddress(targetAssetKey, funXyzAsset);
				} catch (err) {
					console.error('Failed to create deposit address:', err);
				}
			})();
		}
	}, [
		isWalletConnected,
		selectedReceiveAsset?.symbol,
		selectedAssetSymbol,
		selectedChainId,
		availableReceiveAssets,
		createDepositAddress,
		supportedAssets,
	]);

	// Fetch quote for $100k worth of the asset to show price impact
	useEffect(() => {
		if (
			!receivingAddress ||
			!selectedReceiveAsset ||
			!selectedAssetSymbol ||
			!selectedChainId
		)
			return;

		const targetAssetKey = selectedReceiveAsset.symbol as SupportedTargetAsset;
		const targetConfig = SOLANA_TARGET_ASSETS[targetAssetKey];
		if (!targetConfig) return;

		// Find the source asset
		const sourceAsset = supportedAssets.find(
			(a) => a.symbol === selectedAssetSymbol && a.chainId === selectedChainId
		);
		if (!sourceAsset) return;

		// Calculate $100k worth in base units
		// For most tokens, we estimate based on common prices
		const QUOTE_AMOUNT_USD = 100000;
		const getBaseUnitsFor100k = (asset: FunXyzAsset): string => {
			const decimals = asset.decimals;
			// Stablecoins: $1 each
			if (
				[
					'USDC',
					'USDT',
					'DAI',
					'BUSD',
					'USDP',
					'USDS',
					'USDbC',
					'USDe',
				].includes(asset.symbol)
			) {
				return (BigInt(QUOTE_AMOUNT_USD) * BigInt(10 ** decimals)).toString();
			}
			// ETH/WETH: ~$3000
			if (['ETH', 'WETH'].includes(asset.symbol)) {
				const amount = Math.floor(QUOTE_AMOUNT_USD / 3000);
				return (BigInt(amount) * BigInt(10 ** decimals)).toString();
			}
			// BTC/WBTC: ~$100000
			if (['BTC', 'WBTC', 'cbBTC', 'BTCB'].includes(asset.symbol)) {
				const amount = Math.floor(QUOTE_AMOUNT_USD / 100000);
				return (
					BigInt(Math.max(amount, 1)) * BigInt(10 ** decimals)
				).toString();
			}
			// SOL: ~$200
			if (['SOL'].includes(asset.symbol)) {
				const amount = Math.floor(QUOTE_AMOUNT_USD / 200);
				return (BigInt(amount) * BigInt(10 ** decimals)).toString();
			}
			// Default: assume $1 per token (conservative for smaller tokens)
			return (BigInt(QUOTE_AMOUNT_USD) * BigInt(10 ** decimals)).toString();
		};

		const fromAmountBaseUnit = getBaseUnitsFor100k(sourceAsset);

		fetchQuote({
			fromAmountBaseUnit,
			fromChainId: selectedChainId,
			fromTokenAddress: sourceAsset.address,
			recipientAddress: receivingAddress,
			toChainId: '1151111081099710', // Solana
			toTokenAddress: targetConfig.address,
		}).catch(() => {
			// Silently fail - we'll show a fallback in the UI
		});
	}, [
		receivingAddress,
		selectedReceiveAsset?.symbol,
		selectedAssetSymbol,
		selectedChainId,
		supportedAssets,
		fetchQuote,
	]);

	const showBack = returnToDepositModal || createNewSubaccount;

	const handleBack = () => {
		if (returnToDepositModal) {
			show({
				type: 'deposit',
				props: {
					collateralIndex,
					collateralPoolId,
					targetAccountKey: selectedTargetAccountKey,
					defaultLargestBalance,
				},
			});
		} else if (createNewSubaccount) {
			show({
				type: 'new_subaccount',
				props: {
					collateralIndex,
					collateralPoolId,
					depositAmount: newSubaccountDepositAmount,
					depositAccountSelection: newSubaccountDepositSource,
					customAccountName: newSubaccountCustomName,
				},
			});
		} else {
			hide();
		}
	};

	const handleCopy = async () => {
		if (!receivingAddress) return;

		try {
			await navigator.clipboard.writeText(receivingAddress);
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);

			// Track the copy action with route details
			captureEvent('funxyz_copy_deposit_address', {
				from_chain_id: selectedChainId,
				from_chain_name: selectedChain?.name,
				from_asset_symbol: selectedAssetSymbol,
				to_chain: 'Solana',
				to_asset_symbol: selectedReceiveAsset.symbol,
				receiving_address: receivingAddress,
			});
		} catch (error) {
			console.error('Failed to copy:', error);
		}
	};

	const handleTargetAccountChange = useCallback(
		(newAccountKey: string) => setSelectedTargetAccountKey(newAccountKey),
		[]
	);

	const handleChainSelect = (chainId: string) => {
		const chain = chainOptions.find((c) => c.id === chainId);
		// Only allow selection if chain is available for selected asset
		if (chain?.isAvailable) {
			setSelectedChainId(chainId);
		}
	};

	const chainMatchesAddress =
		!selectedChainId ||
		!selectedSourceAsset ||
		selectedChainId === selectedSourceAsset.chainId;
	const isAddressLoading =
		isCreatingAddress || !receivingAddress || !chainMatchesAddress;

	return (
		<BaseModal.Container
			onClose={hide}
			open={true}
			className="sm:max-w-[700px] sm:min-w-[480px]"
		>
			<BaseModal.Title
				onClose={hide}
				showBack={showBack}
				onBack={showBack ? handleBack : undefined}
			>
				Deposit from Other Sources
			</BaseModal.Title>
			<BaseModal.Body>
				{/* How to Deposit expandable section */}
				<div className="flex w-full items-center justify-between gap-2">
					<button
						type="button"
						onClick={() => setIsHowToDepositExpanded(!isHowToDepositExpanded)}
						className="flex shrink-0 cursor-pointer items-center gap-2 typo-b4 text-text-secondary transition-colors hover:text-text-default"
					>
						How to Deposit
						{isHowToDepositExpanded ? (
							<ChevronUp size={16} />
						) : (
							<ChevronDown size={16} />
						)}
					</button>
					<Tooltip
						content="Minimum deposit amount required for the selected asset and chain."
						className="inline-flex items-center"
					>
						<span
							className={`inline-flex items-center gap-xs typo-b4 text-star-yellow ${TOOLTIP_TRIGGER_DECORATION_CLASSNAME}`}
						>
							<Warning color={getColorVar('star-yellow')} size={16} />
							Minimum deposit: $30
						</span>
					</Tooltip>
				</div>

				{isHowToDepositExpanded && (
					<div className="bg-container-bg-secondary flex flex-col gap-3 rounded-sm p-md">
						{!createNewSubaccount && (
							<div className="flex gap-2">
								<div className="flex size-5 flex-shrink-0 items-center justify-center rounded-full bg-text-tertiary">
									<span className="typo-b5 font-bold text-container-bg">1</span>
								</div>
								<div className="flex flex-col gap-1">
									<span className="text-left typo-b4 font-semibold text-text-default">
										Select Deposit Account
									</span>
									<span className="text-left typo-b5 text-text-secondary">
										Choose which Drift account to receive the funds.
									</span>
								</div>
							</div>
						)}

						<div className="flex gap-2">
							<div className="flex size-5 flex-shrink-0 items-center justify-center rounded-full bg-text-tertiary">
								<span className="typo-b5 font-bold text-container-bg">
									{createNewSubaccount ? '1' : '2'}
								</span>
							</div>
							<div className="flex flex-col gap-1">
								<span className="text-left typo-b4 font-semibold text-text-default">
									Select Deposit Account
								</span>
								<span className="text-left typo-b5 text-text-secondary">
									Choose which Drift account to receive the funds.
								</span>
							</div>
						</div>

						<div className="flex gap-2">
							<div className="flex size-5 flex-shrink-0 items-center justify-center rounded-full bg-text-tertiary">
								<span className="typo-b5 font-bold text-container-bg">2</span>
							</div>
							<div className="flex flex-col gap-1">
								<span className="text-left typo-b4 font-semibold text-text-default">
									Select Transfer Asset
								</span>
								<span className="text-left typo-b5 text-text-secondary">
									Choose the asset and chain you want to transfer from.
								</span>
							</div>
						</div>

						<div className="flex gap-2">
							<div className="flex size-5 flex-shrink-0 items-center justify-center rounded-full bg-text-tertiary">
								<span className="typo-b5 font-bold text-container-bg">
									{createNewSubaccount ? '2' : '3'}
								</span>
							</div>
							<div className="flex flex-col gap-1">
								<span className="text-left typo-b4 font-semibold text-text-default">
									Select Receiving Asset
								</span>
								<span className="text-left typo-b5 text-text-secondary">
									{hasSingleReceiveAsset
										? 'Only assets available in your target pool can be received.'
										: 'Pick the asset to receive in your Drift account (supports USDC and SOL).'}
								</span>
							</div>
						</div>

						<div className="flex gap-2">
							<div className="flex size-5 flex-shrink-0 items-center justify-center rounded-full bg-text-tertiary">
								<span className="typo-b5 font-bold text-container-bg">
									{createNewSubaccount ? '3' : '4'}
								</span>
							</div>
							<div className="flex flex-col gap-1">
								<span className="text-left typo-b4 font-semibold text-text-default">
									Transfer & Receive
								</span>
								<span className="text-left typo-b5 text-text-secondary">
									Send the asset to the receiving address — funds arrive in your
									Drift account within 1 minute.
								</span>
							</div>
						</div>
					</div>
				)}

				{!createNewSubaccount && (
					<div className="flex flex-col gap-2">
						<span className="typo-t4 text-text-default">1. Deposit To</span>
						<SubAccountSelector
							selectedTargetAccountKey={selectedTargetAccountKey}
							selectedPoolId={selectedPoolId}
							handleTargetAccountChange={handleTargetAccountChange}
							mode="deposit"
							newSubaccount={{ isNewAccount: false }}
							includeDelegates
							accountLabel="Deposit to"
							filterAccount={filterAccountForFunXyz}
						/>
					</div>
				)}

				{/* Deposit From */}
				<div className="flex flex-col gap-2">
					<span className="typo-t4 text-text-default">
						{createNewSubaccount ? '1' : '2'}. Deposit From
					</span>

					{isLoadingAssets ? (
						<div className="flex items-center justify-center py-4">
							<InlineLoadingBar />
						</div>
					) : (
						<div className="flex gap-2">
							{/* Asset Selector with Search */}
							<ComboBox.Root
								open={assetDropdownOpen}
								onOpenChange={setAssetDropdownOpen}
							>
								<ComboBox.Trigger
									label="Asset"
									className="flex-1"
									disabled={!selectedAsset}
								>
									{selectedAsset && (
										<div className="flex items-center gap-2">
											<AssetIcon
												logo={selectedAsset.logo}
												symbol={selectedAsset.symbol}
												chainId={
													selectedChainId ??
													Object.keys(selectedAsset.addressByChainId)[0]
												}
												address={
													selectedChainId
														? selectedAsset.addressByChainId[selectedChainId]
														: Object.values(selectedAsset.addressByChainId)[0]
												}
												className="size-5"
											/>
											<span className="typo-t3 text-text-default">
												{selectedAsset.symbol}
											</span>
										</div>
									)}
								</ComboBox.Trigger>
								<ComboBox.Options
									id="deposit-asset-combobox"
									query={assetSearchQuery}
									setQuery={setAssetSearchQuery}
									value={selectedAssetSymbol ?? ''}
									onValueChange={(symbol) => {
										setSelectedAssetSymbol(symbol);
										setAssetDropdownOpen(false);
									}}
									searchBarPlaceholder="Search assets..."
									align="start"
									sideOffset={8}
									className="w-[240px]"
									scrollContainerClassName="max-h-[280px]"
								>
									{filteredAssets.map((asset) => (
										<ComboBox.Option
											key={asset.symbol}
											value={asset.symbol}
											isSelected={asset.symbol === selectedAssetSymbol}
											onValueChange={() => {
												setSelectedAssetSymbol(asset.symbol);
												setAssetDropdownOpen(false);
											}}
										>
											<div className="flex items-center gap-2">
												<AssetIcon
													logo={asset.logo}
													symbol={asset.symbol}
													chainId={
														(selectedChainId &&
														asset.addressByChainId[selectedChainId]
															? selectedChainId
															: Object.keys(asset.addressByChainId)[0]) ??
														undefined
													}
													address={
														(selectedChainId &&
															asset.addressByChainId[selectedChainId]) ??
														Object.values(asset.addressByChainId)[0]
													}
													className="size-5"
												/>
												<span>{asset.symbol}</span>
											</div>
										</ComboBox.Option>
									))}
								</ComboBox.Options>
							</ComboBox.Root>

							{/* Chain Selector */}
							<Select.Root
								value={selectedChainId ?? ''}
								onValueChange={handleChainSelect}
							>
								<Select.Trigger label="Chain" className="flex-1">
									{selectedChain && (
										<div className="flex items-center gap-2 overflow-hidden">
											<MarketIcon
												marketSymbol={
													getChainIconSymbol(selectedChain.id) ?? ''
												}
												className="size-5 flex-shrink-0 rounded-full"
											/>
											<span className="truncate typo-t3 text-text-default">
												{selectedChain.name}
											</span>
										</div>
									)}
								</Select.Trigger>
								<Select.Options
									className="max-h-[280px] w-[240px]"
									align="start"
								>
									{chainOptions.map((chain) => (
										<Select.Option
											key={chain.id}
											value={chain.id}
											disabled={!chain.isAvailable}
										>
											<div className="flex items-center gap-2 overflow-hidden">
												<MarketIcon
													marketSymbol={getChainIconSymbol(chain.id) ?? ''}
													className={cj(
														'size-5 flex-shrink-0 rounded-full',
														!chain.isAvailable && 'opacity-50'
													)}
												/>
												<span
													className={cj(
														'truncate',
														!chain.isAvailable && 'text-text-disabled'
													)}
												>
													{chain.name}
												</span>
											</div>
										</Select.Option>
									))}
								</Select.Options>
							</Select.Root>
						</div>
					)}
				</div>

				{/* Receive As */}
				<div className="flex flex-col gap-2">
					<div className="flex flex-col gap-1">
						<span className="typo-t4 text-text-default">
							{createNewSubaccount ? '2' : '3'}. Receive in Drift Account as
						</span>
					</div>

					{availableReceiveAssets.length === 0 ? (
						<div className="rounded-sm border border-input-border bg-input-bg p-md">
							<span className="typo-b4 text-text-secondary">
								Your selected account&apos;s pool does not support USDC or SOL.
								Please select a different account to receive deposits.
							</span>
						</div>
					) : hasSingleReceiveAsset && availableReceiveAssets[0] ? (
						<div className="flex items-center gap-2 rounded-sm border border-input-border bg-input-bg p-md">
							<MarketIcon
								marketSymbol={availableReceiveAssets[0].symbol}
								className="size-5 rounded-full"
							/>
							<span className="typo-t3 text-text-default">
								{availableReceiveAssets[0].symbol}
							</span>
						</div>
					) : (
						<Select.Root
							value={selectedReceiveAsset.symbol}
							onValueChange={(symbol) => {
								const asset = availableReceiveAssets.find(
									(a) => a.symbol === symbol
								);
								if (asset) setSelectedReceiveAsset(asset);
							}}
						>
							<Select.Trigger label="Asset">
								<div className="flex items-center gap-2">
									<MarketIcon
										marketSymbol={selectedReceiveAsset.symbol}
										className="size-5 rounded-full"
									/>
									<span className="typo-t3 text-text-default">
										{selectedReceiveAsset.symbol}
									</span>
								</div>
							</Select.Trigger>
							<Select.Options className="w-[200px]" align="end">
								{availableReceiveAssets.map((asset) => (
									<Select.Option key={asset.symbol} value={asset.symbol}>
										<div className="flex items-center gap-2">
											<MarketIcon
												marketSymbol={asset.symbol}
												className="size-5 rounded-full"
											/>
											<span>{asset.symbol}</span>
										</div>
									</Select.Option>
								))}
							</Select.Options>
						</Select.Root>
					)}

					{availableReceiveAssets.length > 0 && (
						<span className="typo-b5 text-text-secondary">
							Once the transfer is complete, you&apos;ll receive{' '}
							{selectedReceiveAsset.symbol} directly in your Drift account.
						</span>
					)}
				</div>

				{/* Copy Address */}
				<div className="flex flex-col gap-3">
					<span className="typo-t4 text-text-default">
						{createNewSubaccount ? '3' : '4'}. Copy Address
					</span>

					{availableReceiveAssets.length === 0 ? (
						<div className="rounded-sm border border-input-border bg-input-bg p-md">
							<span className="typo-b4 text-text-secondary">
								Select an account that supports USDC or SOL to generate a
								deposit address.
							</span>
						</div>
					) : (
						<>
							<div className="flex gap-3">
								{/* QR Code */}
								<div className="relative flex h-32 w-32 flex-shrink-0 items-center justify-center rounded-sm bg-white p-2">
									{receivingAddress && !isAddressLoading ? (
										<QRCode
											value={receivingAddress}
											size={122}
											level="H"
											includeMargin={false}
										/>
									) : (
										<div className="bg-container-bg-secondary h-full w-full animate-pulse rounded" />
									)}
									{/* Chain icon overlay */}
									{selectedChain && getChainIconSymbol(selectedChain.id) && (
										<div className="pointer-events-none absolute inset-0 flex items-center justify-center">
											<MarketIcon
												marketSymbol={
													getChainIconSymbol(selectedChain.id) ?? ''
												}
												className="size-8"
											/>
										</div>
									)}
								</div>

								{/* Address Section */}
								<div className="flex flex-1 flex-col justify-between overflow-hidden rounded-sm border border-input-border bg-container-bg">
									<div className="flex flex-col gap-2 p-md">
										<Tooltip content="Send any accepted asset to this address and it will automatically bridge, swap, and deposit to your Drift Account.">
											<span
												className={`typo-b4 text-text-secondary ${TOOLTIP_TRIGGER_DECORATION_CLASSNAME}`}
											>
												Receiving Address
											</span>
										</Tooltip>

										{isAddressLoading ? (
											<InlineLoadingBar />
										) : (
											<span className="typo-b3 break-all text-text-default">
												{receivingAddress}
											</span>
										)}
									</div>

									<Button
										variant="secondary"
										size="md"
										className="w-full rounded-none rounded-b-sm border-0 border-t border-input-border"
										disabled={isAddressLoading}
										onClick={handleCopy}
									>
										{copied ? (
											<>
												<span>Copied</span>
												<Checkmark size={16} />
											</>
										) : (
											<>
												<span>Copy</span>
												<Copy size={16} />
											</>
										)}
									</Button>
								</div>
							</div>
							<span className="typo-b5 text-text-secondary">
								Only use this receiving address for this subaccount. It will
								also change depending on which assets you chose to receive in
								Drift in step {createNewSubaccount ? '2' : '3'}.
							</span>
						</>
					)}
				</div>

				<Divider />

				{/* Info Section */}
				<div className="flex flex-col gap-1">
					<div className="flex items-center justify-between">
						<span className="typo-b4 text-text-secondary">Processing Time</span>
						<span className="typo-b4 text-text-default">&lt; 1 min</span>
					</div>
					<div className="flex items-center justify-between">
						<Tooltip
							content={
								<div className="flex flex-col gap-1">
									<span>
										Price Impact reflects the difference between expected and
										execution price, factoring in trade size, liquidity, and
										slippage during execution. Estimated based on $100k
										transfer.
									</span>
								</div>
							}
						>
							<span
								className={`typo-b4 text-text-secondary ${TOOLTIP_TRIGGER_DECORATION_CLASSNAME}`}
							>
								Price Impact
							</span>
						</Tooltip>
						<span className="typo-b4 text-text-default">
							{isGettingQuote
								? 'Estimating...'
								: quote?.estFeeBreakdown?.totalImpact !== undefined
									? `~${quote.estFeeBreakdown.totalImpact.toFixed(2)}%`
									: '-'}
						</span>
					</div>
					{(createNewSubaccount || userAccountNotInitialized) && (
						<div className="flex items-center justify-between">
							<span className="typo-b4 text-text-secondary">
								Reclaimable account setup fee
							</span>
							<span className="typo-b4 text-text-default">
								{NEW_ACCOUNT_BASE_RENT.toNum().toFixed(2)} SOL
							</span>
						</div>
					)}
				</div>
			</BaseModal.Body>
		</BaseModal.Container>
	);
};

export default DepositFromOtherSourcesModal;
